import React from 'react';
import { Card, Space, Tag, Typography, Divider, Badge, Alert } from 'antd';
import { CheckCircleOutlined, FileTextOutlined, PictureOutlined, SoundOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TAILessonPromptingConfig } from '../../../models/courses/aiLessonPrompting';
import AILessonPromptingStudent from '../student/AILessonPromptingStudent';
import './AILessonPromptingTeacher.css';

const { Title, Paragraph, Text } = Typography;

interface AILessonPromptingTeacherProps {
  config: TAILessonPromptingConfig;
  statement?: string;
}

const AILessonPromptingTeacher: React.FC<AILessonPromptingTeacherProps> = ({ config, statement }) => {
  const { t } = useTranslation();

  const getRenderTypeIcon = () => {
    switch (config.renderType) {
      case 'text': return <FileTextOutlined />;
      case 'image': return <PictureOutlined />;
      case 'audio': return <SoundOutlined />;
      default: return null;
    }
  };

  const getRenderTypeLabel = () => {
    switch (config.renderType) {
      case 'text': return 'Texte';
      case 'image': return 'Image';
      case 'audio': return 'Audio';
      default: return '';
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        message="Vue enseignant - Test de la leçon"
        description="Vous pouvez tester la prompt comme un élève. La solution attendue est affichée ci-dessous pour référence."
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
        style={{ borderRadius: 8 }}
      />
      
      <Card title="Tester la leçon" className="ai-prompting-teacher-preview-card">
        <AILessonPromptingStudent config={config} statement={statement} />
      </Card>

      <Card 
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
            <span style={{ fontSize: '16px', fontWeight: 600 }}>
              {t('aiPrompting.teacher.solution') || 'Solution attendue'}
            </span>
          </Space>
        }
        className="ai-prompting-teacher-solution-card"
      >
        <div className="ai-prompting-solution-content">
          {/* Answer Section - Most Prominent */}
          <div className="ai-prompting-solution-answer">
            <div className="ai-prompting-solution-header">
              <Space>
                {getRenderTypeIcon()}
                <Title level={5} style={{ margin: 0 }}>
                  Réponse complète
                </Title>
              </Space>
              <Tag icon={getRenderTypeIcon()} color="blue" className="ai-prompting-solution-type-tag">
                {getRenderTypeLabel()}
              </Tag>
            </div>
            
            <div className="ai-prompting-solution-display">
              {config.renderType === 'text' && (
                <div className="ai-prompting-solution-text">
                  {config.answerTexts && config.answerTexts.length > 0 ? (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      {config.answerTexts.map((text, index) => (
                        <Card
                          key={index}
                          size="small"
                          title={
                            <Space>
                              <Text strong>
                                {t('aiPrompting.teacher.textForKeywords', { count: index + 1 }) ||
                                  `Texte pour ${index + 1} mot-clé${index + 1 > 1 ? 's' : ''} trouvé${index + 1 > 1 ? 's' : ''}`}
                              </Text>
                              {config.answerTexts && index === config.answerTexts.length - 1 && (
                                <Tag color="success" icon={<CheckCircleOutlined />}>
                                  {t('aiPrompting.teacher.finalText') || 'Texte final'}
                                </Tag>
                              )}
                            </Space>
                          }
                          style={{ marginBottom: 8 }}
                        >
                          <Paragraph className="ai-prompting-solution-text-content" style={{ margin: 0 }}>
                            {text}
                          </Paragraph>
                        </Card>
                      ))}
                    </Space>
                  ) : config.answerText ? (
                    // Fallback to old answerText format for backward compatibility
                    <Paragraph className="ai-prompting-solution-text-content">
                      {config.answerText}
                    </Paragraph>
                  ) : (
                    <Alert
                      message={t('aiPrompting.teacher.tryAgain') || 'Aucune réponse configurée. Veuillez configurer au moins un texte.'}
                      type="warning"
                      showIcon
                    />
                  )}
                </div>
              )}
              
              {config.renderType === 'audio' && (
                <div className="ai-prompting-solution-audio">
                  {config.answerAudio ? (
                    <div className="ai-prompting-solution-audio-wrapper">
                      <audio 
                        src={config.answerAudio} 
                        controls 
                        className="ai-prompting-solution-audio-content"
                      />
                    </div>
                  ) : (
                    <Alert
                      message="Aucun audio configuré"
                      type="info"
                      showIcon
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Keywords Section */}
          <Divider className="ai-prompting-solution-divider" />
          
          <div className="ai-prompting-solution-keywords">
            <div className="ai-prompting-solution-keywords-header">
              <Title level={5} style={{ margin: 0 }}>
                {t('aiPrompting.teacher.keywords') || 'Mots-clés requis'}
              </Title>
              <Space>
                <Badge 
                  count={config.keywords?.length || 0} 
                  showZero
                  style={{ backgroundColor: '#1890ff' }}
                  title="Nombre total de mots-clés"
                />
                {config.requiredKeywords > 0 && (
                  <Badge 
                    count={config.requiredKeywords} 
                    showZero
                    style={{ backgroundColor: '#52c41a' }}
                    title="Mots-clés requis minimum"
                  />
                )}
              </Space>
            </div>
            
            {config.keywords && config.keywords.length > 0 ? (
              <>
                <div className="ai-prompting-solution-keywords-list">
                  {config.keywords.map((kw, index) => {
                    const isRequired = index < (config.requiredKeywords || config.keywords.length);
                    return (
                      <Tag 
                        key={kw}
                        color={isRequired ? 'success' : 'default'}
                        icon={isRequired ? <CheckCircleOutlined /> : undefined}
                        className="ai-prompting-solution-keyword-tag"
                      >
                        {kw}
                        {isRequired && <span className="ai-prompting-solution-keyword-required"> (requis)</span>}
                      </Tag>
                    );
                  })}
                </div>
                
                {config.requiredKeywords > 0 && (
                  <div className="ai-prompting-solution-keywords-info">
                    <Text type="secondary">
                      Les élèves doivent trouver au moins <strong>{config.requiredKeywords}</strong> mot{config.requiredKeywords > 1 ? 's' : ''}-clé{config.requiredKeywords > 1 ? 's' : ''} pour révéler complètement la réponse.
                    </Text>
                  </div>
                )}
              </>
            ) : (
              <Alert
                message="Aucun mot-clé configuré"
                description="Ajoutez des mots-clés dans la configuration de la leçon."
                type="warning"
                showIcon
                className="ai-prompting-solution-no-keywords"
              />
            )}
          </div>
        </div>
      </Card>
    </Space>
  );
};

export default AILessonPromptingTeacher;
